import React, { useState, useEffect, useCallback } from 'react';
import { newsAPI } from '../../services/api';
import './NewsAndUpdates.css';

function News() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (activeFilter === 'all') {
        response = await newsAPI.getAllNews({ limit: 50 });
      } else {
        response = await newsAPI.getNewsByCategory(activeFilter, { limit: 50 });
      }

      setUpdates(response.data || []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news updates. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const getCategoryStyle = (category) => {
    const styles = {
      announcement: { bg: '#2d4a2c', color: '#7bc67b' },
      patch: { bg: '#4a3d2c', color: '#d4b976' },
      update: { bg: '#2c3e4a', color: '#76b9d4' },
      feature: { bg: '#4a2c4a', color: '#c976d4' }
    };
    return styles[category] || styles.update;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <div className="news-updates-section welcome-split-layout">
      {/* LEFT (2/3): Welcome */}
      <div className="welcome-left">
        <div className="welcome-card">
          <div className="welcome-header">
            <h2>Welcome to The Ascended</h2>
            <p className="welcome-subtitle">
              A post-apocalyptic tabletop RPG and campaign platform where player stories help shape a shared world.
            </p>
          </div>

          <div className="welcome-body">
            <p>
              <strong>The Ascended</strong> takes place centuries after the Aetherion Shield saved Earth from a cosmic
              catastrophe—then poisoned it. Now the world is broken, humanity is changed, and survival has become a
              political weapon.
            </p>

            <div className="welcome-panels">
              <div className="welcome-panel">
                <h3>What is this platform?</h3>
                <ul>
                  <li>
                    A centralized place to manage <strong>characters</strong>, <strong>campaigns</strong>, and{' '}
                    <strong>source material</strong>.
                  </li>
                  <li>A home for your group’s play: invites, sheets, and <strong>live sessions</strong>.</li>
                  <li>A living foundation for a larger community—where the best stories can become canon.</li>
                </ul>
              </div>

              <div className="welcome-panel">
                <h3>Who survives the Aetherion age?</h3>
                <ul>
                  <li>
                    <strong>Normal</strong> — grit, skill, equipment.
                  </li>
                  <li>
                    <strong>Tolerant</strong> — can harness Aetherion… with consequences.
                  </li>
                  <li>
                    <strong>Ascended</strong> — born with supernatural abilities, hunted and feared.
                  </li>
                </ul>
              </div>
            </div>

            <div className="welcome-panel welcome-panel-wide">
              <h3>Quick Start</h3>
              <ol>
                <li>Create a character in <strong>My Characters</strong>.</li>
                <li>Accept a campaign invite in <strong>Campaigns</strong>.</li>
                <li>
                  When your GM launches a session, click <strong>Join Active Session</strong> on your campaign card.
                </li>
              </ol>
              <ul className="quickstart-links">
                <li>
                  📘{' '}
                  <a
                    href={`${process.env.REACT_APP_API_URL}/downloads/quickstart/The_Ascended_Quickstart_v1_2.pdf`}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ascended Quickstart Guide (PDF)
                  </a>
                </li>

                 <li>
                  📘{' '}
                  <a
                    href={`${process.env.REACT_APP_API_URL}/downloads/quickstart/The_Ascended_Lore_Primer.pdf`}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ascended Lore Primer (PDF)
                  </a>
                </li>

                <li>
                  🎭{' '}
                  <a
                    href={`${process.env.REACT_APP_API_URL}/downloads/quickstart/Scenarios/TheFalseAscended/TheFalseAscended.zip`}
                    download
                  >
                    The False Ascended – Quickstart Scenario (ZIP)
                  </a>
                </li>

                <li>
                  🌊{' '}
                  <a
                    href={`${process.env.REACT_APP_API_URL}/downloads/quickstart/Scenarios/AUsefulLie/AUsefulLie.zip`}
                    download
                  >
                    A Useful Lie – Quickstart Scenario (ZIP)
                  </a>
                </li>
              </ul>
              <p className="welcome-note">
                If you’re a GM: create a campaign, invite your players, then hit <strong>Launch Campaign</strong> to start an active session.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT (1/3): News */}
      <div className="welcome-right">
        <div className="news-right-card">
          <div className="news-right-header">
            <div>
              <h2>📰 News & Updates</h2>
              <p className="library-subtitle">Stay informed about the latest changes and announcements</p>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="news-filters">
            <button
              className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('all')}
            >
              All Updates
            </button>
            <button
              className={`filter-btn ${activeFilter === 'announcement' ? 'active' : ''}`}
              onClick={() => handleFilterChange('announcement')}
            >
              Announcements
            </button>
            <button
              className={`filter-btn ${activeFilter === 'patch' ? 'active' : ''}`}
              onClick={() => handleFilterChange('patch')}
            >
              Patches
            </button>
            <button
              className={`filter-btn ${activeFilter === 'update' ? 'active' : ''}`}
              onClick={() => handleFilterChange('update')}
            >
              Updates
            </button>
            <button
              className={`filter-btn ${activeFilter === 'feature' ? 'active' : ''}`}
              onClick={() => handleFilterChange('feature')}
            >
              New Features
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <p>Loading news updates...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchNews} className="btn-primary">
                Try Again
              </button>
            </div>
          )}

          {/* Updates List */}
          {!loading && !error && (
            <div className="updates-list">
              {updates.length === 0 ? (
                <div className="empty-state">
                  <p>No updates in this category yet.</p>
                </div>
              ) : (
                updates.map((update) => (
                  <article key={update.id} className="update-card">
                    <div className="update-header">
                      <div className="update-title-section">
                        <h3>
                          {update.isPinned && <span className="pin-icon">📌 </span>}
                          {update.title}
                        </h3>
                        <span
                          className="category-badge"
                          style={{
                            backgroundColor: getCategoryStyle(update.category).bg,
                            color: getCategoryStyle(update.category).color
                          }}
                        >
                          {update.category}
                        </span>
                      </div>
                      <div className="update-meta">
                        <span className="update-date">{formatDate(update.date)}</span>
                        <span className="update-author">by {update.author}</span>
                      </div>
                    </div>
                    <div className="update-content">
                      {(update.content || '').split('\n\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  </article>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default News;
